(() => {
  const CONFIG_SELECTOR = '[data-stickerflex-gallery-autoplay-config]';
  const MOBILE_MEDIA_QUERY = '(max-width: 767px)';
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

  class StickerFlexGalleryAutoplay {
    constructor(configElement) {
      this.configElement = configElement;
      this.container = configElement.closest('.product__gallery-container');
      this.gallery = this.container?.querySelector('media-gallery');
      this.slider = this.gallery?.querySelector('slider-element[id^="SliderGallery-"]');
      this.nextButton = this.gallery?.querySelector('button[is="next-button"]');
      this.thumbnails = Array.from(this.gallery?.querySelectorAll('.product__thumbnail') || []);

      if (!this.container || !this.gallery || !this.slider || !this.nextButton) return;

      try {
        this.config = JSON.parse(configElement.textContent);
      } catch (error) {
        return;
      }

      this.delay = Math.max(3000, Number(this.config.delay) || 5000);
      this.mode = this.config.mode || 'return_start';
      this.mobileMedia = window.matchMedia(MOBILE_MEDIA_QUERY);
      this.reducedMotionMedia = window.matchMedia(REDUCED_MOTION_QUERY);
      this.timer = null;
      this.isIntersecting = false;
      this.isHovered = false;
      this.isFocused = false;
      this.stoppedByInteraction = false;
      this.completed = false;

      this.onPointerDown = this.onPointerDown.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onPointerEnter = this.onPointerEnter.bind(this);
      this.onPointerLeave = this.onPointerLeave.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onViewportChange = this.onViewportChange.bind(this);

      this.container.addEventListener('pointerdown', this.onPointerDown, { passive: true });
      this.container.addEventListener('keydown', this.onKeyDown);
      this.container.addEventListener('pointerenter', this.onPointerEnter, { passive: true });
      this.container.addEventListener('pointerleave', this.onPointerLeave, { passive: true });
      this.container.addEventListener('focusin', this.onFocusIn);
      this.container.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.mobileMedia.addEventListener('change', this.onViewportChange);

      this.observer = new IntersectionObserver(
        (entries) => {
          this.isIntersecting = entries.some((entry) => entry.isIntersecting);
          this.update();
        },
        { threshold: 0.35 }
      );
      this.observer.observe(this.gallery);
    }

    isEnabledForViewport() {
      if (this.mobileMedia.matches) return this.config.enableMobile !== false;
      return this.config.enableDesktop !== false;
    }

    canRun() {
      const availableSlides = this.slider.querySelectorAll('.product__media:not([hidden])').length;

      return (
        availableSlides > 1 &&
        this.isEnabledForViewport() &&
        !this.reducedMotionMedia.matches &&
        !this.stoppedByInteraction &&
        !this.completed &&
        !this.isHovered &&
        !this.isFocused &&
        this.isIntersecting &&
        document.visibilityState === 'visible'
      );
    }

    update() {
      this.clearTimer();
      if (!this.canRun()) return;

      this.timer = window.setTimeout(() => this.advance(), this.delay);
    }

    advance() {
      this.timer = null;
      if (!this.canRun()) return;

      if (this.nextButton.disabled) {
        if (this.mode === 'loop') {
          this.resetToFirst();
          this.update();
          return;
        }

        if (this.mode === 'return_start') this.resetToFirst();
        this.completed = true;
        return;
      }

      this.nextButton.click();
      this.update();
    }

    resetToFirst() {
      const firstThumbnail = this.thumbnails.find((thumbnail) => {
        return !thumbnail.hasAttribute('hidden') && !thumbnail.closest('[hidden]');
      });

      firstThumbnail?.click();
    }

    stopForInteraction() {
      this.stoppedByInteraction = true;
      this.clearTimer();
    }

    clearTimer() {
      if (this.timer === null) return;
      window.clearTimeout(this.timer);
      this.timer = null;
    }

    onPointerDown(event) {
      if (event.isTrusted) this.stopForInteraction();
    }

    onKeyDown(event) {
      if (event.isTrusted) this.stopForInteraction();
    }

    onPointerEnter() {
      this.isHovered = true;
      this.update();
    }

    onPointerLeave() {
      this.isHovered = false;
      this.update();
    }

    onFocusIn() {
      this.isFocused = true;
      this.update();
    }

    onFocusOut(event) {
      if (this.container.contains(event.relatedTarget)) return;
      this.isFocused = false;
      this.update();
    }

    onVisibilityChange() {
      this.update();
    }

    onViewportChange() {
      this.update();
    }
  }

  const initialize = (root = document) => {
    root.querySelectorAll(CONFIG_SELECTOR).forEach((configElement) => {
      if (configElement.dataset.autoplayInitialized === 'true') return;
      configElement.dataset.autoplayInitialized = 'true';
      new StickerFlexGalleryAutoplay(configElement);
    });
  };

  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
